import { useState } from "react";
import { Loader2, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { axiosInstance, useAuth } from "@/pages/auth/auth.context";
import { toast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface EventWaitlistJoinProps {
  eventId: string;
  hasWaitlist: boolean;
  maxWaitlistSize: number | null;
  currentWaitlistSize: number | null;
  userOnWaitlist: boolean;
}

export default function EventWaitlistJoin({
  eventId,
  hasWaitlist,
  maxWaitlistSize,
  currentWaitlistSize,
  userOnWaitlist,
}: EventWaitlistJoinProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isWaitlistLoading, setIsWaitlistLoading] = useState(false);

  const joinWaitlist = async () => {
    setIsWaitlistLoading(true);
    try {
      const response = await axiosInstance.post(
        `waitlist/event/${eventId}/join`,
      );

      if (response.data.success) {
        toast({
          title: "Joined Waitlist",
          description:
            response.data.message || "Successfully joined the waitlist!",
          variant: "success",
        });
        // Refresh the event data to update waitlist status
        queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to join waitlist.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error joining waitlist:", error);
      let errorMessage = "Failed to join waitlist.";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsWaitlistLoading(false);
    }
  };

  const leaveWaitlist = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    setIsWaitlistLoading(true);
    try {
      const response = await axiosInstance.delete(
        `waitlist/event/${eventId}/user/${user.id}`,
      );

      if (response.data.success) {
        toast({
          title: "Left Waitlist",
          description:
            response.data.message || "Successfully left the waitlist.",
          variant: "success",
        });
        // Refresh the event data to update waitlist status
        queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to leave waitlist.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error leaving waitlist:", error);
      let errorMessage = "Failed to leave waitlist.";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsWaitlistLoading(false);
    }
  };

  // Don't render if waitlist doesn't exist or is full
  if (
    !hasWaitlist ||
    !maxWaitlistSize ||
    currentWaitlistSize === null ||
    maxWaitlistSize <= currentWaitlistSize
  ) {
    return null;
  }

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Users className="w-5 h-5" />
          Join Event Waitlist
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground">
          Are all the positions you are eligible for full? Join the waitlist to
          get emails when there are new openings!
        </p>
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Waitlist spots available: {maxWaitlistSize - currentWaitlistSize} of{" "}
            {maxWaitlistSize}
          </div>
          {userOnWaitlist ? (
            <Button
              onClick={leaveWaitlist}
              disabled={isWaitlistLoading}
              variant="outline"
              className="gap-2"
            >
              {isWaitlistLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Users className="w-4 h-4" />
              )}
              Leave Waitlist
            </Button>
          ) : (
            <Button
              onClick={joinWaitlist}
              disabled={isWaitlistLoading}
              className="gap-2"
            >
              {isWaitlistLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Users className="w-4 h-4" />
              )}
              Join Waitlist
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
